import React, { useRef, useState } from 'react';

import
{
  Text,
  Icon,
  Button,
  Avatar,
  MoreActions,
  ChangeProfilePanel,
  ChangePasswordPanel,
  ProjectStatistics,
  Tooltip,
} from '@ui-library';

import type { INavbarProps } from './Navbar.types';

import { IconName } from '../Icon/Icon.type';
import { useAuth } from '../../contexts/AuthContext';
import { IMoreActionItem } from '../MoreActions/MoreActions.types';
import { useNavigate, useLocation } from 'react-router-dom';

import ViewCreditsModal from '../CreditViewPanel';

export const Navbar: React.FC<INavbarProps> = ({
  brandName = 'BlueGenAI',
  breadcrumbs = [],
  creditBalance,
  projectCostDisplay,
  projectDurationDisplay,
  onHomeClick,
  user,
  actions = [],
  className = '',
  projectId,
  currentMode = 'build',
}) => {
  const [userMenuIsOpen, setUserMenuIsOpen] = useState(false);
  const userMenuTriggerRef = useRef<HTMLButtonElement>(null);

  const [isUpdateProfileModalOpen, setIsUpdateProfileModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isViewCreditsModalOpen, setIsViewCreditsModalOpen] = useState(false);
  const [isProjectStatisticsOpen, setIsProjectStatisticsOpen] = useState(false);
  const { logout, hasAdminRole, isAdminOnly, switchRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdminView = location.pathname.startsWith('/admin');

  const handleSwitchToAdmin = () => {
    setUserMenuIsOpen(false);
    switchRole('admin');
    navigate('/admin');
  };

  const handleSwitchToUser = () => {
    setUserMenuIsOpen(false);
    switchRole('user');
    navigate('/dashboard');
  };

  const items: IMoreActionItem[] = [
    {
      id: 'update-profile',
      label: 'Update Profile',
      icon: <Icon name={IconName.User} size="sm" variant="secondary" />,
      onClick: () => setIsUpdateProfileModalOpen(true),
    },
    {
      id: 'view-credits',
      label: 'My Credits',
      icon: <Icon name={IconName.Dollar} size="sm" variant="secondary" />,
      onClick: () => {
        setIsViewCreditsModalOpen(true);
      },
    },
    {
      id: 'change-password',
      label: 'Change Password',
      icon: <Icon name={IconName.Lock} size="sm" variant="secondary" />,
      onClick: () => setIsChangePasswordModalOpen(true),
    },
    ...(hasAdminRole && !isAdminOnly ? [
      {
        type: 'separator' as const,
        label: '',
      },
      isAdminView
        ? {
            id: 'switch-to-user',
            label: 'Switch to User',
            icon: <Icon name={IconName.User} size="sm" variant="secondary" />,
            onClick: handleSwitchToUser,
          }
        : {
            id: 'switch-to-admin',
            label: 'Switch to Admin',
            icon: <Icon name={IconName.Shield} size="sm" variant="secondary" />,
            onClick: handleSwitchToAdmin,
          },
    ] : []),
    {
      type: 'separator',
      label: '',
    },
    {
      id: 'logout',
      label: 'Logout',
      icon: <Icon name={IconName.LogOut} size="sm" variant="secondary" />,
      onClick: () => {
        logout();
      },
    }
  ];

  return (
    <nav 
      className={`flex items-center gap-bg-v2-xl px-bg-v2-6xl py-0 h-16 border-b border-border-secondary bg-bg-primary ${className}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <button
        onClick={onHomeClick}
        className="flex items-center gap-bg-v2-md cursor-pointer"
        aria-label="Go to home"
      >
        <Icon name={IconName.BrandLogo} className='h-8' />
        <span
          className="text-text-brand-primary font-semibold"
          style={{
            fontFamily: 'var(--Font-family-font-family-body, Inter)',
            fontSize: 'var(--Font-size-text-lg, 18px)',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: 'var(--Line-height-text-lg, 28px)',
          }}
        >
          {brandName}
        </span>
      </button>
      {/* Left Section - Logo, Brand, Breadcrumbs */}
      <div className="flex items-center gap-bg-v2-xl shrink-0">
        {/* Logo and Brand Name */}
        {/* Vertical Divider */}
        {breadcrumbs.length > 0 && (
          <div className="w-px h-5 bg-border-tertiary shrink-0" aria-hidden="true" />
        )}

        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <nav aria-label="Breadcrumb" className="flex flex-col items-start shrink-0">
            <ol className="flex flex-wrap items-center gap-x-bg-v2-md">
              {/* Home Icon */}
              <li className="flex items-center">
                <Tooltip content="Home" delay={200} position="bottom">
                  <button
                    onClick={onHomeClick}
                    className="flex items-center gap-bg-v2-md hover:opacity-70 transition-opacity"
                    aria-label="Home"
                  >
                    <Icon name={IconName.Home} size="sm" className='text-text-primary' inheritColor />
                  </button>
                </Tooltip>
              </li>

              {/* Breadcrumb Items */}
              {breadcrumbs.map((item, index) => (
                <React.Fragment key={index}>
                  <li className="flex items-center gap-bg-v2-md">
                    {/* Chevron Separator */}
                    <Icon name={IconName.ChevronRight} size="sm" className="text-text-quaternary" inheritColor />
                    {item.onClick ? (
                      <button
                        onClick={item.onClick}
                        className="hover:text-text-secondary transition-colors"
                      >
                        <Text variant="text-sm" weight="medium" color="quaternary">
                          {item.label}
                        </Text>
                      </button>
                    ) : (
                      <Text variant="text-sm" weight="medium" color="quaternary">
                        {item.label}
                      </Text>
                    )}
                  </li>
                </React.Fragment>
              ))}
            </ol>
          </nav>
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1 min-w-px" />

      {/* Right Section - Actions and User */}
      <div className="flex items-center gap-bg-v2-xs shrink-0">
        {/* Project Cost & Duration */}
        {(projectCostDisplay || projectDurationDisplay) && (
          <Tooltip content="Project Statistics" delay={200} position="bottom">
            <button
              onClick={() => setIsProjectStatisticsOpen(true)}
              className="flex items-center gap-bg-v2-md px-bg-v2-lg py-bg-v2-md bg-bg-brand-primary-alt rounded-bg-v2-md shrink-0 hover:bg-bg-primary-hover transition-colors cursor-pointer"
            >
              {projectCostDisplay && (
                <div className="flex items-center">
                  <Icon name={IconName.DollarCircle} size="md" className='text-text-brand-primary' inheritColor />
                  <div className="pl-bg-v2-xs">
                    <Text variant="text-sm" weight="medium" color="brand-primary" className="whitespace-nowrap">
                      {projectCostDisplay}
                    </Text>
                  </div>
                </div>
              )}
              {projectCostDisplay && projectDurationDisplay && (
                <Text variant="text-sm" color="brand-primary">|</Text>
              )}
              {projectDurationDisplay && (
                <div className="flex items-center">
                  <Icon name={IconName.ClockCircle} size="md" className='text-text-brand-primary' inheritColor />
                  <div className="pl-bg-v2-xs">
                    <Text variant="text-sm" weight="medium" color="brand-primary" className="whitespace-nowrap">
                      {projectDurationDisplay}
                    </Text>
                  </div>
                </div>
              )}
            </button>
          </Tooltip>
        )}
        {/* Legacy creditBalance fallback */}
        {creditBalance && !projectCostDisplay && !projectDurationDisplay && (
          <Tooltip content="Project Statistics" delay={200} position="bottom">
            <button
              onClick={() => setIsProjectStatisticsOpen(true)}
              className="flex items-center px-bg-v2-lg py-bg-v2-md bg-bg-brand-primary-alt rounded-bg-v2-md shrink-0 hover:bg-bg-primary-hover transition-colors cursor-pointer"
            >
              <Icon name={IconName.DollarCircle} size="md" className='text-text-brand-primary' inheritColor />
              <div className="pl-bg-v2-md">
                <Text variant="text-sm" weight="medium" color="brand-primary" align="center" className="whitespace-nowrap">
                  {creditBalance}
                </Text>
              </div>
            </button>
          </Tooltip>
        )}

      {actions.map((action, index) => (
        <Button
          key={index}
          label={action.label}
          onClick={action.onClick}
          variant="ghost"
          size="small"
          title={action.tooltip}
          tooltipPosition="bottom"
          className="!rounded-full !p-bg-v2-lg"
        />
      ))}

        {/* User Menu Button */}
        {user && (
          <div className="relative inline-block">
            <button
              ref={userMenuTriggerRef}
              onClick={() => setUserMenuIsOpen(!userMenuIsOpen)}
              className="flex items-center justify-center h-9 px-bg-v2-md rounded-bg-v2-md hover:bg-bg-primary-hover transition-colors"
              aria-label="User menu"
            >
              <Avatar 
                user={user}
                size="md"
                variant="brand"
                showName={true}
              />
            </button>
            <MoreActions
              position="bottom-right"
              items={items}
              isOpen={userMenuIsOpen}
              onClose={() => setUserMenuIsOpen(false)}
              triggerRef={userMenuTriggerRef}
              className='justify-center items-center'
            />
          </div>
        )}
      </div>
      <ChangeProfilePanel
        isOpen={isUpdateProfileModalOpen}
        onClose={() => setIsUpdateProfileModalOpen(false)}
        onSuccess={() => {}}
      />
      <ChangePasswordPanel
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
      <ViewCreditsModal
        isOpen={isViewCreditsModalOpen}
        onClose={() => setIsViewCreditsModalOpen(false)}
      />
      <ProjectStatistics
        isOpen={isProjectStatisticsOpen}
        onClose={() => setIsProjectStatisticsOpen(false)}
        projectId={projectId}
        currentMode={currentMode}
      />
    </nav>
  );
};